import { AnimatePresence } from 'framer-motion';
import subDays from 'date-fns/subDays';
import startOfYear from 'date-fns/startOfYear';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ScSparkline from '../../components/molecules/ScSparkline';
import styled from 'styled-components';

import {
  IconBank,
  IconCalendar,
  IconCash,
  IconFinance,
  IconDownload,
  IconArrowForward,
  IconRefresh,
} from '../../assets/svg/icons';
import Button from '../../components/atoms/Button';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import ButtonExternalLink from '../../components/atoms/Button/buttonExternalLink';
import SimpleSelect from '../../components/atoms/Fields/Select/templates/SimpleSelect';
import Loader from '../../components/atoms/Loader';
import { LoaderMode } from '../../components/atoms/Loader/enums';
import Placeholder from '../../components/molecules/Placeholder';
import { PlaceholderSize } from '../../components/molecules/Placeholder/enums';
import Table from '../../components/molecules/Table';
import FinancialsEntitySelectModal from '../../components/organisms/Modal/templates/FinancialsEntitySelectModal';
import TransactionSummaryModal from '../../components/organisms/Modal/templates/TransactionsSummaryModal';
import { StyledBox } from '../../components/styles/Grid/styles';
import ScCard from '../../components/atoms/ScCard';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import useToggle from '../../hooks/useToggle';
import { statementsTableColumns, yearDropdownOptions } from './consts';
import { formatIqd } from '../../utils/money';
import { formatDateShort } from '../../utils/date';
import { EntityContextProvider } from './EntityContext';
import {
  useAvailableBalance,
  usePendingBalance,
  useStatementList,
  useWithdrawnBalance,
  useYearDropdownHandlers,
} from './hooks';
import { useDashboardStats } from '../dashboard/hooks';
import WithdrawnBalancesModal from '../../components/organisms/Modal/templates/WithdrawnBalancesModal';
import { mq } from '../../theme/mediaQueries';

/* ─── Balance Card ─────────────────────────────────────────── */
type BalanceCardAccent = 'green' | 'info' | 'warning';

const ACCENT_BORDER: Record<BalanceCardAccent, string> = {
  green: 'var(--success)',
  info: 'var(--info)',
  warning: 'var(--warning)',
};
const ACCENT_ICON_BG: Record<BalanceCardAccent, string> = {
  green: 'var(--success-soft)',
  info: 'var(--info-soft)',
  warning: 'var(--warning-soft)',
};
const ACCENT_ICON_COLOR: Record<BalanceCardAccent, string> = {
  green: 'var(--success-ink)',
  info: 'var(--info-ink)',
  warning: 'var(--warning-ink)',
};

const StyledBalanceCard = styled.div<{ $accent: BalanceCardAccent }>`
  background: var(--surface);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  padding: var(--s-5);
  padding-top: calc(var(--s-5) + 3px);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $accent }) => ACCENT_BORDER[$accent]};
  }
`;

const StyledIconTile = styled.div<{ $accent: BalanceCardAccent }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--r-md);
  background: ${({ $accent }) => ACCENT_ICON_BG[$accent]};
  color: ${({ $accent }) => ACCENT_ICON_COLOR[$accent]};
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;
  }
`;

const StyledBalanceEyebrow = styled.div`
  font-size: 10px;
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--muted);
`;

const StyledBalanceValue = styled.div`
  font-size: var(--t-3xl);
  font-weight: 700;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
  line-height: 1;

  span {
    font-size: var(--t-sm);
    font-weight: var(--weight-medium);
    color: var(--muted);
    margin-left: var(--s-1);
    letter-spacing: 0;
  }
`;

const StyledBalanceSub = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  line-height: 1.4;
`;

const StyledBalanceCardFooter = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 14px;
`;

/* ─── Balance Cards Row ─────────────────────────────────────── */
const StyledBalanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--s-4);
  margin-bottom: var(--s-6);

  ${mq.ltLg} {
    grid-template-columns: 1fr;
  }
`;

/* ─── Cash Flow Card ────────────────────────────────────────── */
const StyledCashFlowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--s-4);
  gap: var(--s-4);
  flex-wrap: wrap;
`;

const StyledCashFlowTitle = styled.div`
  font-size: var(--t-base);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  letter-spacing: -0.01em;
`;

const StyledCashFlowSub = styled.div`
  font-size: var(--t-xs);
  color: var(--muted);
  margin-top: var(--s-1);
`;

const StyledWindowTabs = styled.div`
  display: flex;
  gap: 4px;
  background: var(--paper-2);
  border-radius: var(--r-sm);
  padding: 3px;
`;

const StyledWindowTab = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
  font-size: var(--t-xs);
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  background: ${({ $active }) => ($active ? 'var(--surface)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--ink)' : 'var(--muted)')};
  box-shadow: ${({ $active }) => ($active ? 'var(--sh-1)' : 'none')};
  transition: all 140ms ease;

  &:hover:not([aria-pressed='true']) {
    color: var(--ink-2);
  }
`;

const StyledLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-5);
  margin-bottom: var(--s-3);
  flex-wrap: wrap;
`;

const StyledLegendItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--t-xs);
  color: var(--muted);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
  }

  strong {
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    font-weight: var(--weight-semibold);
  }
`;

/* ─── Statements Section ────────────────────────────────────── */
const StyledStatementsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--s-4);
  margin-bottom: var(--s-4);
  flex-wrap: wrap;
`;

const StyledStatementsTitleBlock = styled.div``;

const StyledStatementsTitle = styled.h2`
  font-size: var(--t-xl);
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--ink);
  margin: 0 0 var(--s-1);
`;

const StyledStatementsSub = styled.div`
  font-size: var(--t-sm);
  color: var(--muted);
`;

const StyledStatementsActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-2);
`;

/* ─── Types ─────────────────────────────────────────────────── */
type CashFlowWindow = '7D' | '30D' | '90D' | 'YTD' | 'All';
const CASH_FLOW_WINDOWS: CashFlowWindow[] = ['7D', '30D', '90D', 'YTD', 'All'];

/* ─── Helpers ─────────────────────────────────────────────── */
const sumField = (rows: any[], key: string): number =>
  rows.reduce((acc, r) => acc + (r[key] ?? 0), 0);

/* ─── SVG Cash Flow Chart ────────────────────────────────────── */
interface CashFlowEntry { date: string; revenue: number; }

const fmtDateLabel = (iso: string, locale = 'en'): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso.slice(5, 10);
    const l = locale === 'ar' ? 'ar-IQ' : 'en-GB';
    return d.toLocaleDateString(l, { day: 'numeric', month: 'short' });
  } catch { return iso.slice(5, 10); }
};

const CashFlowSvg: React.FC<{ data: CashFlowEntry[]; payouts?: number[] }> = ({ data, payouts = [] }) => {
  const intl = useIntl();
  const W = 880, H = 220, pad = { l: 48, r: 12, t: 8, b: 24 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  if (data.length < 2) return <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'dashboard.noData', defaultMessage: 'No data yet' })}</div>;

  const inflow = data.map(d => d.revenue);
  const pay = payouts.length === inflow.length ? payouts : inflow.map(() => 0);
  const max = Math.max(...inflow, ...pay, 1) * 1.1;
  const dx = innerW / (inflow.length - 1);

  const linePath = (arr: number[]) => arr.map((v, i) => {
    const x = pad.l + i * dx, y = pad.t + innerH - (v / max) * innerH;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const areaPath = (arr: number[]) =>
    `${linePath(arr)} L${(pad.l + innerW).toFixed(1)},${(pad.t + innerH).toFixed(1)} L${pad.l},${(pad.t + innerH).toFixed(1)} Z`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(max * t));
  const fmt = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);

  const n = data.length;
  const labelIdxs = [0, Math.round(n * 0.25), Math.round(n * 0.5), Math.round(n * 0.75), n - 1];

  return (
    <div style={{ overflow: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: 560, display: 'block' }}>
        <defs>
          <linearGradient id="cfg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand-green)" stopOpacity={0.18} />
            <stop offset="100%" stopColor="var(--brand-green)" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="cfp" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--info)" stopOpacity={0.14} />
            <stop offset="100%" stopColor="var(--info)" stopOpacity={0} />
          </linearGradient>
        </defs>
        {yTicks.map((t, i) => {
          const y = pad.t + innerH - (t / max) * innerH;
          return (
            <g key={i}>
              <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="var(--line)" strokeDasharray="2 4" />
              <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize={10} fill="var(--muted)" fontFamily="var(--font-mono)">{fmt(t)}</text>
            </g>
          );
        })}
        <path d={areaPath(inflow)} fill="url(#cfg)" />
        <path d={linePath(inflow)} fill="none" stroke="var(--brand-green)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {labelIdxs.map((idx, i) => (
          <text key={i} x={pad.l + idx * dx} y={H - 6} textAnchor={i === labelIdxs.length - 1 ? 'end' : 'middle'} fontSize={10} fill="var(--muted)" fontFamily="var(--font-mono)">
            {fmtDateLabel(data[idx]?.date ?? '', intl.locale)}
          </text>
        ))}
      </svg>
    </div>
  );
};

const FinancialsPage: React.FC = () => {
  const intl = useIntl();

  const [selectedWindow, setSelectedWindow] = useState<CashFlowWindow>('30D');

  const cashFlowParams = useMemo(() => {
    const now = new Date();
    let from: Date;
    switch (selectedWindow) {
      case '7D':
        from = subDays(now, 7);
        break;
      case '30D':
        from = subDays(now, 30);
        break;
      case '90D':
        from = subDays(now, 90);
        break;
      case 'YTD':
        from = startOfYear(now);
        break;
      case 'All':
      default:
        from = subDays(now, 365);
        break;
    }
    return { from, to: now };
  }, [selectedWindow]);

  const { data: cashFlowData, isLoading: cashFlowLoading } = useDashboardStats(cashFlowParams);

  const dailyBreakdown = cashFlowData?.response?.dailyBreakdown ?? [];

  const totalInflow = useMemo(() => sumField(dailyBreakdown, 'revenue'), [dailyBreakdown]);
  const totalPayouts = 0;
  const netFlow = totalInflow - totalPayouts;
  const inflowSpark = useMemo(() => dailyBreakdown.map((d: CashFlowEntry) => d.revenue), [dailyBreakdown]);

  const { setHeaderTitle } = useHeaderTitleContext();
  const [selectedYear, handleYearChange] = useYearDropdownHandlers();
  const { statements, loading } = useStatementList(selectedYear);

  const [isOpen, { toggleOn, toggleOff }] = useToggle();
  const [
    isEntitySelectModalOpen,
    {
      toggleOn: toggleEntitySelectModalOn,
      toggleOff: toggleEntitySelectModalOff,
    },
  ] = useToggle();
  const [
    viewWithdrawnDetailsModal,
    {
      toggleOn: toggleViewWithdrawnDetailsModalOn,
      toggleOff: toggleViewWithdrawnDetailsModalOff,
    },
  ] = useToggle();

  const {
    pendingBalance,
    isLoading: pendingBalanceLoading,
    currency: pendingBalanceCurrency,
  } = usePendingBalance();
  const {
    availableBalance,
    isLoading: availableBalanceLoading,
    currency: availableBalanceCurrency,
  } = useAvailableBalance();
  const {
    withdrawnBalance,
    isLoading: withdrawnBalanceLoading,
    currency: withdrawnBalanceCurrency,
    withdrawnBalances,
  } = useWithdrawnBalance();

  const tableColumns = useMemo(() => statementsTableColumns(intl), [intl]);

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.financials',
        defaultMessage: 'Financials',
      })
    );
  }, [intl, setHeaderTitle]);

  const balanceLoading =
    availableBalanceLoading || pendingBalanceLoading || withdrawnBalanceLoading;

  return (
    <EntityContextProvider>
      {/* Page header */}
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.financials', defaultMessage: 'Financials' })}
        subtitle={intl.formatMessage({ id: 'financials.pageSubtitle', defaultMessage: 'Balances, statements and payouts — IQD' })}
        actions={
          <>
            <Button
              kind={ButtonKind.Outline}
              icon={IconRefresh}
              onClick={() => {/* refresh */}}
            >
              {intl.formatMessage({ id: 'financials.refresh', defaultMessage: 'Refresh' })}
            </Button>
            <Button
              kind={ButtonKind.Primary}
              icon={IconCash}
              onClick={toggleEntitySelectModalOn}
            >
              <FormattedMessage id='financials.cashOut' defaultMessage='Cash out' />
            </Button>
          </>
        }
      />

      {/* Balance cards */}
      {balanceLoading ? (
        <Loader mode={LoaderMode.FinancialBalanceBoxes} />
      ) : (
        <StyledBalanceGrid data-tour='financials-balances'>
          {/* Current balance */}
          <StyledBalanceCard $accent='green'>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <StyledIconTile $accent='green'><IconBank /></StyledIconTile>
              <div style={{ flex: 1, minWidth: 0 }}>
                <StyledBalanceEyebrow>{intl.formatMessage({ id: 'financials.currentBalance', defaultMessage: 'Current balance' })}</StyledBalanceEyebrow>
                <StyledBalanceValue>
                  {formatIqd(availableBalance)}
                  <span>{availableBalanceCurrency || 'IQD'}</span>
                </StyledBalanceValue>
                <StyledBalanceSub>{intl.formatMessage({ id: 'financials.availableToCashOut', defaultMessage: 'Available to cash out today' })}</StyledBalanceSub>
              </div>
            </div>
            <StyledBalanceCardFooter>
              <Button kind={ButtonKind.Primary} size={ButtonSize.Small} onClick={toggleEntitySelectModalOn}>
                <FormattedMessage id='financials.cashOut' defaultMessage='Cash out' />
              </Button>
              <ScSparkline data={inflowSpark} color='var(--brand-green)' width={120} height={36} />
            </StyledBalanceCardFooter>
          </StyledBalanceCard>

          {/* Pending balance */}
          <StyledBalanceCard $accent='info'>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <StyledIconTile $accent='info'><IconBank /></StyledIconTile>
              <div style={{ flex: 1, minWidth: 0 }}>
                <StyledBalanceEyebrow>{intl.formatMessage({ id: 'financials.pendingBalance', defaultMessage: 'Pending balance' })}</StyledBalanceEyebrow>
                <StyledBalanceValue>
                  {formatIqd(pendingBalance)}
                  <span>{pendingBalanceCurrency || 'IQD'}</span>
                </StyledBalanceValue>
                <StyledBalanceSub>{intl.formatMessage({ id: 'financials.settlesWithin', defaultMessage: 'Settles within 2–3 business days' })}</StyledBalanceSub>
              </div>
            </div>
            <StyledBalanceCardFooter>
              <Button kind={ButtonKind.Outline} size={ButtonSize.Small}>
                {intl.formatMessage({ id: 'financials.viewPending', defaultMessage: 'View pending' })}
              </Button>
            </StyledBalanceCardFooter>
          </StyledBalanceCard>

          {/* Withdrawn balance */}
          <StyledBalanceCard $accent='warning'>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <StyledIconTile $accent='warning'><IconCash /></StyledIconTile>
              <div style={{ flex: 1, minWidth: 0 }}>
                <StyledBalanceEyebrow>{intl.formatMessage({ id: 'financials.withdrawnBalance', defaultMessage: 'Withdrawn balance' })}</StyledBalanceEyebrow>
                <StyledBalanceValue>
                  {formatIqd(withdrawnBalance)}
                  <span>{withdrawnBalanceCurrency || 'IQD'}</span>
                </StyledBalanceValue>
                <StyledBalanceSub>{intl.formatMessage({ id: 'financials.thisMonth', defaultMessage: 'This month' })}</StyledBalanceSub>
              </div>
            </div>
            <StyledBalanceCardFooter>
              <Button kind={ButtonKind.Outline} size={ButtonSize.Small} onClick={toggleViewWithdrawnDetailsModalOn}>
                {intl.formatMessage({ id: 'financials.viewDetails', defaultMessage: 'View details' })}
              </Button>
            </StyledBalanceCardFooter>
          </StyledBalanceCard>
        </StyledBalanceGrid>
      )}

      {/* Cash Flow Chart */}
      <ScCard style={{ marginBottom: 'var(--s-6)' }}>
        <StyledCashFlowHeader>
          <div>
            <StyledCashFlowTitle>{intl.formatMessage({ id: 'financials.cashFlow', defaultMessage: 'Cash flow' })}</StyledCashFlowTitle>
            <StyledCashFlowSub>
              {intl.formatMessage({ id: 'financials.dailyInflowSub', defaultMessage: 'Daily inflow vs payouts · last {days} days' }, { days: selectedWindow === 'All' ? '365' : selectedWindow.replace('D', '') })}
            </StyledCashFlowSub>
          </div>
          <StyledWindowTabs>
            {CASH_FLOW_WINDOWS.map((w) => (
              <StyledWindowTab
                key={w}
                $active={selectedWindow === w}
                aria-pressed={selectedWindow === w}
                onClick={() => setSelectedWindow(w)}
              >
                {w}
              </StyledWindowTab>
            ))}
          </StyledWindowTabs>
        </StyledCashFlowHeader>

        <StyledLegendRow>
          <StyledLegendItem $color='var(--brand-green)'>
            {intl.formatMessage({ id: 'financials.inflow', defaultMessage: 'Inflow' })}&nbsp;<strong>{formatIqd(totalInflow)} IQD</strong>
          </StyledLegendItem>
        </StyledLegendRow>

        {cashFlowLoading ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
            {intl.formatMessage({ id: 'dashboard.loading', defaultMessage: 'Loading…' })}
          </div>
        ) : (
          <CashFlowSvg data={dailyBreakdown} payouts={[]} />
        )}
      </ScCard>

      {/* Statements section */}
      <ScCard pad={false}>
        <StyledStatementsHeader style={{ padding: 'var(--s-4) var(--s-5)', borderBottom: '1px solid var(--line)', marginBottom: 0 }}>
          <StyledStatementsTitleBlock>
            <StyledStatementsTitle>
              <FormattedMessage
                id='financials.previousStatments'
                defaultMessage='Previous statements'
              />
            </StyledStatementsTitle>
            <StyledStatementsSub>{intl.formatMessage({ id: 'financials.settledAndIssued', defaultMessage: 'Settled and issued payouts' })}</StyledStatementsSub>
          </StyledStatementsTitleBlock>
          <StyledStatementsActions>
            <div style={{ minWidth: '6rem' }}>
              <SimpleSelect
                disabled={loading}
                value={selectedYear}
                onChange={handleYearChange}
                options={yearDropdownOptions}
                prefixIcon={IconCalendar}
              />
            </div>
            <Button kind={ButtonKind.Outline} icon={IconDownload}>
              {intl.formatMessage({ id: 'financials.exportAll', defaultMessage: 'Export all' })}
            </Button>
          </StyledStatementsActions>
        </StyledStatementsHeader>

        {statements.length ? (
          loading ? (
            <Loader mode={LoaderMode.Table} />
          ) : (
            <Table columns={tableColumns} {...{ data: statements }} />
          )
        ) : (
          <StyledBox justifyContent='center'>
            <Placeholder
              icon={IconFinance}
              title={intl.formatMessage({
                id: 'search.noResults',
                defaultMessage: 'No results',
              })}
              size={PlaceholderSize.Big}
              onClick={noop}
            />
          </StyledBox>
        )}
      </ScCard>

      <AnimatePresence>
        {isOpen && (
          <TransactionSummaryModal
            onClose={() => {
              toggleEntitySelectModalOff();
              toggleOff();
            }}
            withCloseButton
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isEntitySelectModalOpen && (
          <FinancialsEntitySelectModal
            onClose={toggleEntitySelectModalOff}
            onCashOut={toggleOn}
            withCloseButton
          />
        )}
        {viewWithdrawnDetailsModal && (
          <WithdrawnBalancesModal
            onClose={toggleViewWithdrawnDetailsModalOff}
            withdrawnBalances={withdrawnBalances}
            withCloseButton
          />
        )}
      </AnimatePresence>
    </EntityContextProvider>
  );
};

export default FinancialsPage;
